// The CDS capability-split (conclave_cds_spec §4.1, familiar_spec §5) read against a Covenant's
// `tools` field: every tool is context-bearing (reads private data), egress-bearing (writes to an
// untrusted sink), or neutral. An actor is Inner (context, no egress) or Outer (egress, no
// context) — a Covenant holding both is the write-down violation the split exists to forbid.
// Pure module (no React, no fetch) so the classification is unit-testable.

export type CapabilityClass = 'context' | 'egress' | 'neutral'

/**
 * Known Diwata tools by capability class (conclave_spec §7, cds §4.1).
 * Scry is the canonical Outer/egress tool; Diwa/Vault/Sanctum/Grain read the
 * keeper's private world; Assay verifies (the release authority — neutral).
 */
const TOOL_CLASS: Record<string, CapabilityClass> = {
  scry: 'egress',
  web: 'egress',
  email: 'egress',
  browser: 'egress',
  diwa: 'context',
  vault: 'context',
  sanctum: 'context',
  grain: 'context',
  lore: 'context',
  memory: 'context',
  assay: 'neutral',
  grimoire: 'neutral',
}

/** Permission verbs that are egress regardless of the tool that carries them. */
const EGRESS_PERMISSIONS = /^(send|post|publish|fetch_url|search_web|egress)/

export function classifyTool(name: string, permissions: string[] = []): CapabilityClass {
  const byName = TOOL_CLASS[name.toLowerCase()]
  if (byName === 'egress') return 'egress'
  if (permissions.some((p) => EGRESS_PERMISSIONS.test(p))) return 'egress'
  return byName ?? 'neutral'
}

export interface CapabilityTag {
  tool: string
  permissions: string[]
  cls: CapabilityClass
}

export interface CapabilitySplit {
  /** Context-bearing grants — the Inner side (private world, no egress). */
  context: CapabilityTag[]
  /** Egress-bearing grants — the Outer side (web/API/external channels). */
  egress: CapabilityTag[]
  /** Grants on neither side of the boundary (e.g. Assay verification). */
  neutral: CapabilityTag[]
  /**
   * The Covenant's posture under the split invariant:
   *   inner  — context only (may also hold neutral)
   *   outer  — egress only (may also hold neutral)
   *   mixed  — holds BOTH context and egress: the write-down violation
   *   unbound — no tool grants at all
   */
  posture: 'inner' | 'outer' | 'mixed' | 'unbound'
}

/** Extract the permission list from a Covenant `tools` entry (shape per conclave_spec §7). */
function permissionsOf(entry: unknown): string[] {
  if (entry && typeof entry === 'object' && Array.isArray((entry as { permissions?: unknown }).permissions)) {
    return ((entry as { permissions: unknown[] }).permissions).filter(
      (p): p is string => typeof p === 'string',
    )
  }
  return []
}

export function splitCovenantTools(tools: Record<string, unknown> | null | undefined): CapabilitySplit {
  const context: CapabilityTag[] = []
  const egress: CapabilityTag[] = []
  const neutral: CapabilityTag[] = []

  for (const [tool, entry] of Object.entries(tools ?? {})) {
    const permissions = permissionsOf(entry)
    const cls = classifyTool(tool, permissions)
    const tag: CapabilityTag = { tool, permissions, cls }
    if (cls === 'context') context.push(tag)
    else if (cls === 'egress') egress.push(tag)
    else neutral.push(tag)
  }

  const posture =
    context.length > 0 && egress.length > 0
      ? 'mixed'
      : context.length > 0
        ? 'inner'
        : egress.length > 0
          ? 'outer'
          : 'unbound'

  return { context, egress, neutral, posture }
}
